import { coachApi } from '@shared/api/client';
import type { SiteRecord, PageRecord, SymbolRecord, RevisionDiffResponse, DocumentAST } from './types';
import {
  adaptSiteRecord,
  adaptSiteRecordList,
  adaptPageRecord,
  adaptPageRecordList,
  adaptSymbolRecord,
  adaptSymbolRecordList,
  adaptRevisionDiff,
} from './adapters';

export const editorApi = {
  // Sites
  listSites: async (): Promise<SiteRecord[]> => {
    const raw = await coachApi.get<unknown>('/webbuilder/sites');
    return adaptSiteRecordList(raw);
  },
  createSite: async (data: { name: string; subdomain_slug: string }): Promise<SiteRecord> => {
    const raw = await coachApi.post<unknown>('/webbuilder/sites', data);
    return adaptSiteRecord(raw);
  },
  updateSite: async (
    siteId: string,
    data: { name?: string; status?: string; active_theme_id?: string; settings_json?: Record<string, unknown> }
  ): Promise<SiteRecord> => {
    const raw = await coachApi.patch<unknown>(`/webbuilder/sites/${siteId}`, data);
    return adaptSiteRecord(raw);
  },

  // Pages & Visual Studio
  listPages: async (siteId: string): Promise<PageRecord[]> => {
    const raw = await coachApi.get<unknown>(`/webbuilder/sites/${siteId}/pages`);
    return adaptPageRecordList(raw);
  },
  createPage: async (
    siteId: string,
    data: { title: string; route_path: string; page_type?: string }
  ): Promise<PageRecord> => {
    const raw = await coachApi.post<unknown>(`/webbuilder/sites/${siteId}/pages`, data);
    return adaptPageRecord(raw);
  },
  updatePage: async (
    pageId: string,
    data: { title?: string; route_path?: string }
  ): Promise<{ updated: boolean; page_id: string; title: string; route_path: string }> => {
    return coachApi.patch<{ updated: boolean; page_id: string; title: string; route_path: string }>(
      `/webbuilder/pages/${pageId}`,
      data
    );
  },
  getPage: async (pageId: string): Promise<PageRecord> => {
    const raw = await coachApi.get<unknown>(`/webbuilder/pages/${pageId}`);
    return adaptPageRecord(raw);
  },
  saveDraft: async (
    pageId: string,
    data: { draft_ast: DocumentAST; seo?: Record<string, unknown> }
  ): Promise<PageRecord> => {
    const raw = await coachApi.put<unknown>(`/webbuilder/pages/${pageId}/draft`, data);
    return adaptPageRecord(raw);
  },
  publishPage: (pageId: string): Promise<{ content_hash: string; version_no: number }> =>
    coachApi.post<{ content_hash: string; version_no: number }>(`/webbuilder/pages/${pageId}/publish`, {}),
  rollbackPage: (pageId: string, versionNo: number): Promise<{ rolled_back: boolean }> =>
    coachApi.post<{ rolled_back: boolean }>(`/webbuilder/pages/${pageId}/rollback/${versionNo}`, {}),
  getRevisionDiff: async (pageId: string, versionNo: number): Promise<RevisionDiffResponse> => {
    const raw = await coachApi.get<unknown>(`/webbuilder/pages/${pageId}/revisions/${versionNo}/diff`);
    return adaptRevisionDiff(raw);
  },
  deletePage: (pageId: string): Promise<{ deleted: boolean }> =>
    coachApi.delete<{ deleted: boolean }>(`/webbuilder/pages/${pageId}`),

  // Global Symbols
  listSymbols: async (siteId: string): Promise<SymbolRecord[]> => {
    const raw = await coachApi.get<unknown>(`/webbuilder/sites/${siteId}/symbols`);
    return adaptSymbolRecordList(raw);
  },
  createSymbol: async (
    siteId: string,
    data: { name: string; symbol_key: string; category: string; symbol_ast: unknown }
  ): Promise<SymbolRecord> => {
    const raw = await coachApi.post<unknown>(`/webbuilder/sites/${siteId}/symbols`, data);
    return adaptSymbolRecord(raw);
  },
  updateSymbol: async (
    siteId: string,
    symbolId: string,
    data: { name?: string; symbol_ast?: unknown }
  ): Promise<SymbolRecord> => {
    const raw = await coachApi.put<unknown>(`/webbuilder/sites/${siteId}/symbols/${symbolId}`, data);
    return adaptSymbolRecord(raw);
  },
  deleteSymbol: (siteId: string, symbolId: string): Promise<{ deleted: boolean }> =>
    coachApi.delete<{ deleted: boolean }>(`/webbuilder/sites/${siteId}/symbols/${symbolId}`),
};

export const webbuilderApi = editorApi;
export type * from './types';
